/**
 * Нажатие кнопки мыши, отданное горячей клавише или рации, не должно делать того, что эта кнопка
 * делает обычно. Боковые листают историю — и уводят со страницы вместе с разговором: Chrome
 * листает по отпусканию, так что гасить надо и его. Средняя включает автопрокрутку, а на ссылке
 * открывает её в новом окне (в приложении для компьютера — в браузере). Левая и правая с модификатором
 * нажали бы кнопку под курсором или открыли меню.
 *
 * Поэтому гасим само нажатие, его отпускание и следующий за ними click, auxclick или contextmenu.
 */

/** Кнопка → нажатие, которое мы погасили и чьё отпускание ещё не пришло. */
const pending = new Map<number, MouseEvent>()
let installed = false

// у левой и правой есть обработчики на самих кнопках интерфейса — туда событие не пускаем
function block(e: MouseEvent): void {
  e.preventDefault()
  if (e.button === 0 || e.button === 2) e.stopPropagation()
}

function install(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  // новое нажатие той же кнопки, которое никто не погасил, снимает ожидание: отпускание прошлого
  // могло потеряться, и тогда мы съели бы чужой клик. Сравнение по самому событию не зависит
  // от того, чей обработчик увидел нажатие первым.
  window.addEventListener('mousedown', (e) => {
    const was = pending.get(e.button)
    if (was && was !== e) pending.delete(e.button)
  }, true)
  window.addEventListener('mouseup', (e) => {
    if (pending.has(e.button)) block(e)
  }, true)
  for (const type of ['click', 'auxclick'] as const) {
    window.addEventListener(type, (e) => {
      if (pending.delete(e.button)) block(e)
    }, true)
  }
  // меню по правой кнопке, а на маке и по Ctrl+клику левой — оно приходит сразу за нажатием
  window.addEventListener('contextmenu', (e) => {
    const left = pending.get(0)
    if (pending.has(2) || (left?.ctrlKey && e.timeStamp - left.timeStamp < 1000)) block(e)
  }, true)
}

/** Погасить это нажатие вместе с его отпусканием. */
export function consumePress(e: MouseEvent): void {
  install()
  block(e)
  pending.set(e.button, e)
}

/**
 * Пока действует, у кнопки нет браузерных действий — ни при нажатии, ни при отпускании.
 * Для кнопки рации: пока включён режим рации, она не листает историю и не открывает ссылки,
 * в том числе вне разговора и в приложении для компьютера, где саму кнопку ловит оболочка.
 */
export function guardButton(button: number): () => void {
  if (button < 0 || typeof window === 'undefined') return () => undefined
  const onDown = (e: MouseEvent) => {
    if (e.button === button) consumePress(e)
  }
  // объектом, а не голым true: так снимается и в браузере, и в EventTarget из Node, на котором это проверяют тесты
  const capture = { capture: true }
  window.addEventListener('mousedown', onDown, capture)
  return () => window.removeEventListener('mousedown', onDown, capture)
}
